import { memo } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { Lightbulb } from '@trinity/chat-components/components/Chat.Widget/Lightbulb';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import AuraLogo from '@trinity/chat-components/static/images/aura-icon.svg?url';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';

type UserAuthorProps = {
  author: Chat.Author;
  className?: string;
};

type SystemAuthorProps = {
  animate?: boolean;
  className?: string;
};

export const SystemAuthor = memo(({ animate, className }: SystemAuthorProps) => {
  const { isDigitalTwin, segmentIconUrl } = useAvatarPanel();

  return (
    <SystemAuthorRoot className={className} noPadding={isDigitalTwin && !!segmentIconUrl}>
      {isDigitalTwin ? (
        segmentIconUrl ? (
          <SegmentIcon src={segmentIconUrl} alt="Segment avatar" />
        ) : (
          <AuraIcon src={AuraLogo} alt="" aria-hidden="true" />
        )
      ) : (
        <Lightbulb size={22} />
      )}
    </SystemAuthorRoot>
  );
});

export const UserAuthor = memo(({ author, className }: UserAuthorProps) => {
  return (
    <UserAuthorRoot className={className}>
      {getFirstLetter(author.profile.firstName)} {getFirstLetter(author.profile.lastName)}
    </UserAuthorRoot>
  );
});

function getFirstLetter(name: string) {
  if (!name) return '';

  return name.trim().at(0)?.toUpperCase() ?? '';
}

const AuthorRoot = styled('div')`
  border-radius: 50%;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UserAuthorRoot = styled(AuthorRoot)(
  ({ theme }) => `
  background-color: ${theme.palette.gray.light1};
  color: ${theme.palette.black.main};
  font-size: clamp(12px, 3.6cqi, 18px);
  font-family: ${theme.fonts.semiBold};
  letter-spacing: -2px;
`
);

const SystemAuthorRoot = styled(AuthorRoot, { shouldForwardProp: shouldForwardProp('noPadding') })<{
  noPadding?: boolean;
}>(({ theme, noPadding }) => ({
  backgroundColor: noPadding ? 'transparent' : theme.palette.primary.light6,
  ...(noPadding && { padding: 0 }),
  '& > svg': {
    width: '55%',
    height: 'auto',
  },
}));

const SegmentIcon = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const AuraIcon = styled.img`
  width: 55%;
  height: auto;
  display: block;
`;
